import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { motion } from 'framer-motion'
import { Loader2 } from 'lucide-react'
import { useAuthStore } from '../stores/authStore'

/**
 * Unified entry point. Staff route straight to their console; a customer
 * routes to /client home. Nothing is started from here.
 *
 * DW-CLIENT-HOME-DASHBOARD-ENTRY-KEEPS-WORD-DECOY-ON-HOME: this entry used to
 * read interview status and send an incomplete (or unknown) customer off to
 * /client/interview. ClientDashboard and ClientNavigation had already stopped
 * doing that — an incomplete first meeting stays on /client, with
 * HomeTripwiresCard and its "Download Decoy (.docx)" mounted, and the meeting
 * is offered in place by the held brand + "First meeting" chrome. But anyone
 * landing on the unified entry (/dashboard) was still diverted before they
 * ever reached home: they never saw the tripwires card, never saw the Word
 * decoy, and the first meeting became a second product that hid the canary.
 * DigitalWatch.md: "Canaries are in the same picture — a tripwire the analyst
 * sees with the logs, not a /client gimmick the model never reads." So every
 * customer now lands on /client, whatever their interview status is.
 *
 * That also means there is nothing for this page to ask the backend. The
 * destination does not depend on the answer, so the status read is gone
 * rather than fetched and ignored — a flaky status endpoint can no longer
 * cost a household the canary on the way in. The gate that status still
 * drives lives in ClientDashboard, where it holds the SIEM read-back
 * (/client/alerts, /client/briefing) closed until the meeting is sat. Home is
 * the team and the canaries; it is not the console, so there is no console to
 * hold here. Start is never called from this page either — starting after a
 * complete meeting creates a new meeting forever.
 */
export function DashboardPage() {
  const navigate = useNavigate()
  const principal = useAuthStore((s) => s.principal)
  const isAnalyst = useAuthStore((s) => s.isAnalyst)
  const isAdmin = useAuthStore((s) => s.isAdmin)
  const isLoading = useAuthStore((s) => s.isLoading)

  useEffect(() => {
    if (isLoading) return
    if (!principal) {
      navigate('/login')
      return
    }
    // Staff (analyst or admin) land on the SOC lead console (BRIEF-F1b).
    if (isAdmin || isAnalyst) {
      navigate('/console')
      return
    }
    // Customer (role 'client', including the org administrator): home is
    // /client — the team, the holding picture and the tripwires — complete
    // first meeting or not.
    navigate('/client')
  }, [principal, isAnalyst, isAdmin, isLoading, navigate])

  return (
    <div className="min-h-screen bg-cream flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className="flex flex-col items-center gap-3"
      >
        <Loader2 className="w-8 h-8 text-brass animate-spin" />
        <p className="text-sm text-gray-500">Getting ready…</p>
      </motion.div>
    </div>
  )
}
